// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import capitalize from "lodash/capitalize";
import React, { FC, useState } from "react";
import { css } from "styled-components";
import TrailerTrackingV2Card from "@store/Library/Components/TrailerTrackingV2/TrailerTrackingV2Card";
import { TrailerTrackingV2Model } from "@store/Framework/Types/ApiModels";
import TrailerTrackingV2Pagination from "@store/Library/Components/TrailerTrackingV2/TrailerTrackingV2Pagination";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import translate from "@insite/client-framework/Translate";
import DoubleChevronUp, { DoubleChevronUpOffset } from "@store/Library/Components/Toolkit/Icons/DoubleChevronUp";
import DoubleChevronDown, {
    DoubleChevronDownOffset,
} from "@store/Library/Components/Toolkit/Icons/DoubleChevronDown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import {
    TrailerSortDirection,
    TrailerSortField,
} from "@store/Library/Components/TrailerTrackingV2/TrailerTrackingV2Table";
import TrailerTrackingV2DownloadLink from "@store/Library/Components/TrailerTrackingV2/TrailerTrackingV2DownloadLink";

export interface TrailerTrackingV2CardListProps {
    cards: Array<TrailerTrackingV2Model>;
    showRequestPickupButton?: boolean;
    filteredTrailerCount?: number;
    totalTrailerCount?: number;
}

export interface TrailerTrackingV2CardListStyles {
    actionLink?: LinkPresentationProps;
    actionsWrapper?: InjectableCss;
    collapseLinkIcon?: IconPresentationProps;
    expandLinkIcon?: IconPresentationProps;
    linksWrapper?: InjectableCss;
    listWrapper?: InjectableCss;
    resultCountText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

const linkIconProps = (src: IconPresentationProps["src"], offset?: IconOffset) => ({
    src,
    color: "inherit",
    css: css`
        ${iconScalableStyles("1em", offset)}
        transition: inherit;
    `,
});

export const trailerTrackingV2CardListStyles: TrailerTrackingV2CardListStyles = {
    actionsWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(10))}
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(4)};
            align-items: center;
            justify-content: space-between;
        `,
    },
    resultCountText: resultCountProps.countText,
    linksWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(4)} 0;
        `,
    },
    actionLink: {
        css: css`
            ${linkActionStyles()}
            ${inlineSeparatorStyles({ spacing: pxToRem(20), color: "common.borderLight" })}
        `,
        typographyProps: {
            css: css`
                display: flex;
                gap: ${pxToRem(6)};
                transition: inherit;
            `,
        },
    },
    collapseLinkIcon: linkIconProps(DoubleChevronUp, DoubleChevronUpOffset),
    expandLinkIcon: linkIconProps(DoubleChevronDown, DoubleChevronDownOffset),
    listWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(10)} 0;
        `,
    },
};

const styles = trailerTrackingV2CardListStyles;

const TrailerTrackingV2CardList: FC<TrailerTrackingV2CardListProps> = ({
    cards,
    showRequestPickupButton,
    filteredTrailerCount,
    totalTrailerCount,
}) => {
    const [toggleCollapse, setToggleCollapse] = useState(false);
    const [toggleExpand, setToggleExpand] = useState(false);
    const [sortField, setSortField] = useState<TrailerSortField>("dwellTimeInDays");
    const [sortDirection, setSortDirection] = useState<TrailerSortDirection>("descending");

    const handleSortClick = (field: TrailerSortField) => {
        if (sortField === field) {
            setSortDirection((prev: TrailerSortDirection) => (prev === "descending" ? "ascending" : "descending"));
        } else {
            setSortField(field);
            setSortDirection("descending");
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.actionsWrapper}>
                {typeof totalTrailerCount === "number" && (
                    <Typography {...styles.resultCountText} data-test-selector="trailerTrackingV2_resultCount">
                        {translate("{0} of {1}", String(filteredTrailerCount ?? 0), String(totalTrailerCount))}{" "}
                        {capitalize(translate(totalTrailerCount === 1 ? "trailer" : "trailers"))}
                    </Typography>
                )}
                <StyledWrapper {...styles.linksWrapper}>
                    <TrailerTrackingV2DownloadLink />
                    <Link {...styles.actionLink} onClick={() => setToggleCollapse(!toggleCollapse)}>
                        <Icon {...styles.collapseLinkIcon} />
                        {translate("Collapse All")}
                    </Link>
                    <Link {...styles.actionLink} onClick={() => setToggleExpand(!toggleExpand)}>
                        <Icon {...styles.expandLinkIcon} />
                        {translate("Expand All")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.listWrapper}>
                {cards.map(card => (
                    <TrailerTrackingV2Card
                        data={card}
                        key={card.id}
                        showRequestPickupButton={showRequestPickupButton}
                        toggleCollapse={toggleCollapse}
                        toggleExpand={toggleExpand}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSortClick={handleSortClick}
                    />
                ))}
            </StyledWrapper>
            <TrailerTrackingV2Pagination />
        </StyledWrapper>
    );
};

export default TrailerTrackingV2CardList;
